import { orchestrator } from 'satcheljs';
import { getStore } from '../store/store';
import { differenceInMinutes, addMinutes, OwaDate } from 'owa-datetime';
import { updateTimeSuggestionsWithCriteria } from '../actions/dialogActions';
import { isSuccessStatusCode } from 'owa-http-status-codes';
import { getFindMeetingTimesSuggestionsRequestForReschedule } from '../utils/getFindMeetingTimeSuggestionsRequest';
import FindMeetingTimesResponse from 'owa-calendar-scheduling-service/lib/schema/FindMeetingTimesResponse';
import { getTimeRangeFromTimingSearchCriteria } from '../utils/getTimeRangeFromTimingSearchCriteria';
import { findMeetingTimesService } from 'owa-calendar-scheduling-service/lib/services/findMeetingTimesService';
import { updateDateTimes, logFindMeetingTimesRequestId } from 'owa-calendar-compose-form-actions';
import { updateTimeSuggestions, setTimeSuggestionsRequestState } from '../actions/internalActions';
import { TimeSuggestionsRequestState } from '../store/schema/TimeSuggestionsRequestState';
import logTimeSuggestionsRequestSummary from '../utils/logTimeSuggestionsMetrics';
import {
    shouldShowNoSuggestionsFound,
    showNoSuggestionsFound,
} from '../utils/timeSuggestionsRequestUtils';
import { TimeChangeActionType } from 'owa-calendar-helpers-types/lib/TimeChangeActionType';
import { getDefaultDurationForShortenedDuration } from '../utils/durationOptionsUtils';
import {
    getConfiguredEventStartTimeBasedOnPreviousIntervalWithDuration,
    getConfiguredEventStartTime,
} from 'owa-calendar-cloud-settings-utils';
import { isPreviousAndCurrentDurationAdjusted } from '../utils/shouldAdjustStartEndTimeForDuration';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';

orchestrator(updateTimeSuggestionsWithCriteria, async actionMessage => {
    const { event } = actionMessage;
    const {
        durationSearchCriteria: newDuration,
        timingSearchCriteria,
        shouldAdjustStartTimeEndTime,
    } = getStore();
    const { start: rangeStart, end: rangeEnd } = getTimeRangeFromTimingSearchCriteria(
        event.Start,
        timingSearchCriteria
    );
    const owaStartDate = event.Start;
    let owaEndDate = event.End;
    let currentDuration = differenceInMinutes(owaEndDate, owaStartDate);

    if (currentDuration != newDuration) {
        // We need to adjust the event Start on the time picker to respect shortenedEvent settings
        const adjustedEventStart = getAdjustedStartTime(
            event,
            currentDuration,
            newDuration,
            shouldAdjustStartTimeEndTime
        );
        const adjustedEventEnd = addMinutes(adjustedEventStart, newDuration);

        // Update time picker to display adjusted eventStart/End - This respects shortenedEvent settings.
        updateDateTimes(
            { start: adjustedEventStart, end: adjustedEventEnd },
            TimeChangeActionType.TimeSuggestionsSearchCriteria
        );
        // Get the new event duration after adjusting
        currentDuration = differenceInMinutes(adjustedEventEnd, adjustedEventStart);
    }

    if (shouldShowNoSuggestionsFound(event)) {
        // Show "no time suggestions found" if it's all day/recurring event or if the event has invalid attendees
        showNoSuggestionsFound();
        return;
    }

    // Adjust the event duration to not be affected by shortenedEvent settings for the findmeetingtimesrequest
    // so the start late - end early time ranges are Free for the user.
    // Ex. And adjusted duration of 22 mins (shortenedEvent setting = StartLate by 8 mins)
    // will be requested as a timeslot of 30 mins, and will be shortened later (in convertMeetingSlotIntoOwaDates)
    // for display purposes
    owaEndDate = addMinutes(owaStartDate, getDefaultDurationForShortenedDuration(currentDuration));

    const durationToRequest = differenceInMinutes(owaEndDate, owaStartDate);

    const request = getFindMeetingTimesSuggestionsRequestForReschedule(
        event,
        rangeStart,
        rangeEnd,
        durationToRequest
    );
    setTimeSuggestionsRequestState(TimeSuggestionsRequestState.SearchingSuggestions);
    let response = await findMeetingTimesService(request, 'suggestionsExp');
    if (response.headers?.has('request-id')) {
        logFindMeetingTimesRequestId(response.headers.get('request-id'));
    }

    let timeSuggestions: FindMeetingTimesResponse;
    if (isSuccessStatusCode(response.status)) {
        timeSuggestions = await response.json();
        if (timeSuggestions.MeetingTimeSuggestions?.[0]) {
            updateTimeSuggestions(timeSuggestions);
            setTimeSuggestionsRequestState(TimeSuggestionsRequestState.ShowSuggestions);
        } else {
            // If there are no suggestions returned from the server, then show "no time suggestions found"
            showNoSuggestionsFound();
        }
    } else {
        // If time suggestions failed, then show "no time suggestions found"
        showNoSuggestionsFound();
    }

    logTimeSuggestionsRequestSummary(
        event,
        response.status,
        timeSuggestions?.MeetingTimeSuggestions?.length,
        timeSuggestions?.EmptySuggestionsReason
    );
});

/**
 * Get the adjusted starttime based on shortenedEventsSettings
 */
function getAdjustedStartTime(
    event: CalendarEvent,
    currentDuration: number,
    newDuration: number,
    shouldAdjustStartTimeEndTime: boolean
): OwaDate {
    let eventStart = event.Start;
    if (shouldAdjustStartTimeEndTime) {
        // If we are jumping from one defaultAdjustedDuration no another defaultAdjustedDuration, we need to get the new adjustedStartTime
        // since we can be jumping from something like "Start Late 8 mins" to "Start late 10 mins" based on the new duration of the event
        if (isPreviousAndCurrentDurationAdjusted(currentDuration, newDuration)) {
            eventStart = getConfiguredEventStartTimeBasedOnPreviousIntervalWithDuration(
                event.Start,
                event.End,
                newDuration
            );
        } else {
            // If we are jumping from a custom duration to a defaultAdjustedDuration, we need to get the new adjustedStartTime
            // since we will be jumping from a time that has no shortenedDurationSetting like 8:00am, and we will need it to appear as 8:08am
            // to respect shortenedEvents settings
            const tempEventEnd = addMinutes(
                eventStart,
                getDefaultDurationForShortenedDuration(newDuration)
            );
            eventStart = getConfiguredEventStartTime(event.Start, tempEventEnd);
        }
    }
    return eventStart;
}
